import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronsLeftIcon, ChevronsRightIcon, FileIcon, MailIcon, MessageCircleIcon, PaperclipIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, assignLead, deleteNote, updateLeadStage, updateNoteColor } from '../../services/leadService';
import { NoteColor } from '../../types';
import { NOTE_COLORS, noteColor } from './noteColors';
import { addFile, deleteFile } from '../../services/fileService';
import { answerToText, formatDateTime, fullName } from '../../utils/format';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Switch } from '../ui/Switch';
import { Avatar } from '../ui/Avatar';
import { SourceBadge } from './SourceBadge';
import { StageBadge } from './StageBadge';
interface LeadDrawerProps {
  leadId: string | null;
  onClose: () => void;
}
type PanelState = 'compacto' | 'ampliado';

/** Sección plegable dentro de la ficha del lead. */
function Section({
  title,
  count,
  open,
  onToggle,
  children
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <section className="rounded-xl border border-border" data-id="element-68" data-mp="30whkxhxuqd8p">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-id="element-69" data-mp="30tve5kisc1rj">
        <span className="text-sm font-semibold text-foreground" data-id="element-70" data-mp="288lq6iruevek">
          {title}
          {count !== undefined && <span className="ml-2 rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-muted-foreground" data-id="element-71" data-mp="2d5vic66m9l63">
              {count}
            </span>}
        </span>
        <ChevronDownIcon className={`h-4 w-4 flex-none text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-72" data-mp="1kihrmptihluf" />
      </button>
      {open && <div className="border-t border-border p-4" data-id="element-73" data-mp="1hgd98k43flp3">{children}</div>}
    </section>;
}

/**
 * Panel lateral del lead con tres estados: cerrado, compacto y ampliado.
 * Las secciones son plegables y su estado se mantiene durante la sesión.
 */
export function LeadDrawer({
  leadId,
  onClose
}: LeadDrawerProps) {
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    leads,
    stages,
    sources,
    notes,
    history,
    forms,
    landings,
    files
  } = useWorkspace();
  const fileRef = useRef<HTMLInputElement>(null);
  const [panel, setPanel] = useState<PanelState>('compacto');
  const [essentials, setEssentials] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({
    info: true,
    respuestas: false,
    notas: true,
    historial: false,
    archivos: false
  });
  const [noteTitle, setNoteTitle] = useState('');
  const [noteFeedback, setNoteFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [noteTone, setNoteTone] = useState<NoteColor>('gray');
  const [noteFilter, setNoteFilter] = useState<'all' | NoteColor>('all');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    if (leadId) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [leadId, onClose]);
  const lead = leads.find(l => l.id === leadId);
  if (!lead) return null;
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const stage = stages.find(s => s.id === lead.stageId);
  const leadNotes = notes.filter(n => n.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const leadHistory = history.filter(h => h.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const visibleNotes = noteFilter === 'all' ? leadNotes : leadNotes.filter(n => (n.color ?? 'gray') === noteFilter);
  const leadFiles = files.filter(f => f.leadId === lead.id);
  const canEdit = hasPermission('leads.edit');
  const expanded = panel === 'ampliado';
  const essentialInfo: [string, string][] = [['Empresa', lead.company || '—'], ['Teléfono', lead.phone], ['Tipo de servicio', lead.service || '—'], ['Presupuesto disponible', lead.budget || '—']];
  const fullInfo: [string, string][] = [...essentialInfo, ['Email', lead.email], ['Instagram', lead.instagram || '—'], ['Ciudad', [lead.city, lead.country].filter(Boolean).join(', ') || '—'], ['Web', lead.website || '—'], ['Inversión actual en publicidad', lead.adBudget || '—'], ['Campaña', lead.origin.campaign || '—'], ['Landing', landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'], ['Formulario', forms.find(f => f.id === lead.origin.formId)?.name ?? '—'], ['Fecha de entrada', formatDateTime(lead.createdAt)], ['Último contacto', lead.lastContactAt ? formatDateTime(lead.lastContactAt) : 'Sin contactar']];
  const info = essentials ? essentialInfo : fullInfo;
  async function saveNote() {
    if (!noteBody.trim() || !lead) return;
    setSaving(true);
    setNoteFeedback(null);
    try {
      await addNote(lead.organizationId, lead.id, user?.id ?? 'user', authorName, noteBody.trim(), noteTitle.trim() || undefined, noteTone);
      setNoteTitle('');
      setNoteBody('');
      setNoteTone('gray');
      setNoteFeedback({
        ok: true,
        text: 'Nota guardada.'
      });
    } catch (err) {
      setNoteFeedback({
        ok: false,
        text: err instanceof Error ? err.message : 'No se ha podido guardar la nota.'
      });
    } finally {
      setSaving(false);
    }
  }
  function toggle(key: string) {
    setOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }
  const phoneDigits = lead.phone.replace(/\D/g, '');
  return <>
      <div className="fixed inset-0 z-40 bg-navy/25 backdrop-blur-[2px] lg:bg-navy/10" onClick={onClose} aria-hidden="true" data-id="element-74" data-mp="1q266t4okeyym" />
      <aside role="dialog" aria-label={`Ficha de ${fullName(lead.firstName, lead.lastName)}`} className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl transition-[max-width] duration-300 ${expanded ? 'lg:max-w-[680px]' : 'lg:max-w-[440px]'}`} data-id="element-75" data-mp="3r5qizwyadp0n">
        {/* Cabecera */}
        <header className="flex items-start gap-3 border-b border-border p-4 sm:p-5" data-id="element-76" data-mp="3w4jkss47nrkz">
          <div className="min-w-0 flex-1" data-id="element-77" data-mp="eo1yg5un3m24">
            <p className="truncate text-base font-semibold text-navy" data-id="element-78" data-mp="eqy0a59lyuvu">
              {fullName(lead.firstName, lead.lastName)}
            </p>
            <p className="truncate text-sm text-muted-foreground" data-id="element-79" data-mp="nq1il70yb69a">{lead.company}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-80" data-mp="iqomdvo6wmu1">
              <StageBadge stage={stage} stages={canEdit ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-81" data-mp="ioc5zmy2ykj3" />
              <SourceBadge source={lead.origin.source} sources={sources} data-id="element-82" data-mp="2kibfq3vk0tto" />
            </div>
          </div>
          <div className="flex flex-none items-center gap-1" data-id="element-83" data-mp="zzhf0vobmheh">
            <button type="button" onClick={() => setPanel(expanded ? 'compacto' : 'ampliado')} aria-label={expanded ? 'Contraer panel' : 'Ampliar panel'} className="hidden rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground lg:block" data-id="element-84" data-mp="1023ncxj1d0xr">
              {expanded ? <ChevronsRightIcon className="h-4 w-4" data-id="element-85" data-mp="v7hlbkyugkqc" /> : <ChevronsLeftIcon className="h-4 w-4" data-id="element-86" data-mp="h9j7qhscksbw" />}
            </button>
            <button type="button" onClick={onClose} aria-label="Cerrar panel" className="rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground" data-id="element-87" data-mp="hbchw7265uon">
              <XIcon className="h-4 w-4" data-id="element-88" data-mp="89wfalhliwuz" />
            </button>
          </div>
        </header>

        {/* Acciones rápidas */}
        <div className="grid grid-cols-3 gap-2 border-b border-border p-4 sm:p-5" data-id="element-89" data-mp="cc9bai6jjgek">
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<MessageCircleIcon className="h-4 w-4" data-id="element-91" data-mp="26n67490d3dpk" />} onClick={() => window.open(`https://wa.me/${phoneDigits}`, '_blank', 'noopener')} data-id="element-90" data-mp="22ndw7pnhwvh1">
            WhatsApp
          </Button>
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<MailIcon className="h-4 w-4" data-id="element-93" data-mp="pi3v4e9rtk5y" />} onClick={() => window.open(`mailto:${lead.email}`)} data-id="element-92" data-mp="pf7wenq3mrgg">
            Email
          </Button>
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<PhoneIcon className="h-4 w-4" data-id="element-95" data-mp="bocvpypr7gny" />} onClick={() => window.open(`tel:${lead.phone}`)} data-id="element-94" data-mp="fqhkaj1chn3q">
            Llamar
          </Button>
        </div>

        {/* Contenido */}
        <div className="flex-1 space-y-3 overflow-y-auto p-4 sm:p-5" data-id="element-96" data-mp="6nynafk8q4pe">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-surface px-4 py-3" data-id="element-97" data-mp="1r861owp932e">
            <Switch id="essentials" label="Mostrar solo lo esencial" checked={essentials} onChange={setEssentials} data-id="element-98" data-mp="3sxjbdx8qspzj" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2" data-id="element-99" data-mp="1w23qboh8y6l0">
            <Select id="drawer-stage" label="Estado" disabled={!canEdit} options={stages.map(s => ({
            value: s.id,
            label: s.name
          }))} value={lead.stageId} onChange={e => updateLeadStage(lead.id, e.target.value, authorName)} data-id="element-100" data-mp="1r4v3vbp2fgdy" />
            <Select id="drawer-owner" label="Comercial" placeholder="Sin asignar" disabled={!canEdit} options={members.map(m => ({
            value: m.userId,
            label: fullName(m.firstName, m.lastName)
          }))} value={lead.assignedTo ?? ''} onChange={e => {
            const member = members.find(m => m.userId === e.target.value);
            assignLead(lead.id, e.target.value || undefined, member ? fullName(member.firstName, member.lastName) : '', authorName);
          }} data-id="element-101" data-mp="23lcsawnfb301" />
          </div>

          <Section title="Información principal" open={open.info} onToggle={() => toggle('info')} data-id="element-102" data-mp="1umrbp71ihy1w">
            <dl className="divide-y divide-border" data-id="element-103" data-mp="313d1f0511v5a">
              {info.map(([label, value]) => <div key={label} className="flex items-start justify-between gap-4 py-2.5" data-id="element-104" data-mp="315h7noqef68q">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-105" data-mp="3640s1kpcg9yz">
                    {label}
                  </dt>
                  <dd className="max-w-[60%] break-words text-right text-sm text-foreground" data-id="element-106" data-mp="5xg1d25847pc">
                    {value}
                  </dd>
                </div>)}
            </dl>
          </Section>

          {!essentials && <Section title="Respuestas del formulario" count={lead.answers.length} open={open.respuestas} onToggle={() => toggle('respuestas')} data-id="element-107" data-mp="ep9o56bciend">
              {lead.answers.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-108" data-mp="3j49ioxbsole6">
                  Este lead no tiene respuestas asociadas.
                </p> : <dl className="divide-y divide-border" data-id="element-109" data-mp="3n5lb1am1zkq2">
                  {lead.answers.map(answer => <div key={answer.questionId} className="py-2.5" data-id="element-110" data-mp="3s6ioj9w4bql8">
                      <dt className="text-sm font-medium text-foreground" data-id="element-111" data-mp="12gdz2d2ipbe9">
                        {answer.label}
                      </dt>
                      <dd className="mt-1 text-sm text-muted-foreground" data-id="element-112" data-mp="17dxbd7fnne3k">
                        {answerToText(answer.value) || '—'}
                      </dd>
                    </div>)}
                </dl>}
            </Section>}

          <Section title="Notas" count={leadNotes.length} open={open.notas} onToggle={() => toggle('notas')} data-id="element-113" data-mp="1cm5ib55j5y8n">
            {hasPermission('notes.edit') && <div className="mb-4 space-y-3" data-id="element-114" data-mp="13sadjy8bm9tj">
                <Input id="note-title" label="Título" placeholder="Resumen de la llamada" value={noteTitle} onChange={e => setNoteTitle(e.target.value)} data-id="element-115" data-mp="13rihyneq4c39" />
                <Textarea id="note-body" label="Nota" rows={6} placeholder="Detalles, próximos pasos, objeciones…" value={noteBody} onChange={e => setNoteBody(e.target.value)} data-id="element-116" data-mp="ttbcp1tsrih3" />
                {/* Color de la nota + vista previa antes de guardar */}
                <div data-id="element-117" data-mp="kuzfggyfdmfo">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-118" data-mp="2j8iz7xlsqkil">Color</p>
                  <div className="flex flex-wrap gap-2" data-id="element-119" data-mp="2j66hnljyac62">
                    {NOTE_COLORS.map(tone => <button key={tone.value} type="button" aria-label={tone.label} aria-pressed={noteTone === tone.value} onClick={() => setNoteTone(tone.value)} className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-3 text-sm transition-colors ${noteTone === tone.value ? 'border-foreground font-medium text-foreground' : 'border-border text-muted-foreground'} ${tone.bg}`} data-id="element-120" data-mp="2f7p5uopl4dib">
                        <span className={`h-3 w-3 rounded-full ${tone.dot}`} aria-hidden="true" data-id="element-121" data-mp="7lfdue1y9bn3" />
                        {tone.label}
                      </button>)}
                  </div>
                  <div className={`mt-3 rounded-lg border-l-4 px-3 py-2 text-sm text-foreground ${noteColor(noteTone).bg} ${noteColor(noteTone).border}`} data-id="element-122" data-mp="109b7f9w82nfn">
                    {noteBody.trim() || 'Vista previa de la nota'}
                  </div>
                </div>
                <Button size="sm" className="min-h-[44px] w-full" loading={saving} disabled={!noteBody.trim()} onClick={saveNote} data-id="element-123" data-mp="36zre9aivilfg">
                  {saving ? 'Guardando…' : 'Guardar nota'}
                </Button>
                {noteFeedback && <p role="status" className={`text-sm ${noteFeedback.ok ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-124" data-mp="33gsc0x9wbj6u">
                    {noteFeedback.text}
                  </p>}
              </div>}
            {/* Filtro por color */}
            <div className="mb-3 flex flex-wrap gap-1.5" data-id="element-125" data-mp="3de6u11bgwg19">
              {([{
              value: 'all',
              label: 'Todas'
            }, ...NOTE_COLORS.map(c => ({
              value: c.value,
              label: `${c.label}s`
            }))] as {
              value: 'all' | NoteColor;
              label: string;
            }[]).map(option => <button key={option.value} type="button" aria-pressed={noteFilter === option.value} onClick={() => setNoteFilter(option.value)} className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${noteFilter === option.value ? 'bg-foreground text-background' : 'bg-surface text-muted-foreground hover:text-foreground'}`} data-id="element-126" data-mp="2uiqfatx73cpd">
                  {option.label}
                </button>)}
            </div>
            {visibleNotes.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-127" data-mp="10934liqj2eoi">
                Todavía no hay notas ni comentarios.
              </p> : <ol className="space-y-3" data-id="element-128" data-mp="19bugfjnbfcgo">
                {visibleNotes.map(note => <li key={note.id} className={`rounded-xl border border-border border-l-4 p-3 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-129" data-mp="199hs8oqys5v6">
                    <div className="flex items-center gap-2" data-id="element-130" data-mp="1j0cf9vc1b9ho">
                      <Avatar name={note.authorName} size="sm" data-id="element-131" data-mp="1o0id5t8wds87" />
                      <div className="min-w-0 flex-1" data-id="element-132" data-mp="1nynp462bxjzw">
                        {note.title && <p className="truncate text-sm font-semibold text-foreground" data-id="element-133" data-mp="1t0mt9i5w8kxw">
                            {note.title}
                          </p>}
                        <p className="text-xs text-muted-foreground" data-id="element-134" data-mp="3uqy9v11u49ua">
                          {note.authorName} · {formatDateTime(note.createdAt)}
                        </p>
                      </div>
                      {note.authorId === user?.id && hasPermission('notes.edit') && <button type="button" aria-label="Eliminar nota" onClick={() => deleteNote(note.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-135" data-mp="3rabtvd5ne44j">
                          <Trash2Icon className="h-4 w-4" data-id="element-136" data-mp="3hjh809wbd6jm" />
                        </button>}
                    </div>
                    {/* Cambiar el color de una nota ya guardada */}
                    {hasPermission('notes.edit') && <div className="mt-2 flex gap-1.5" data-id="element-137" data-mp="e04q5j6t5rtw">
                        {NOTE_COLORS.map(tone => <button key={tone.value} type="button" aria-label={`Marcar como ${tone.label}`} aria-pressed={note.color === tone.value} onClick={() => updateNoteColor(note.id, tone.value)} className={`h-6 w-6 rounded-full ${tone.dot} transition-transform hover:scale-110 ${note.color === tone.value ? 'ring-2 ring-foreground ring-offset-2' : 'opacity-50'}`} data-id="element-138" data-mp="9477h682o9dy" />)}
                      </div>}
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground" data-id="element-139" data-mp="2krnc6uhf63ts">
                      {note.body}
                    </p>
                  </li>)}
              </ol>}
          </Section>

          {!essentials && <Section title="Historial y actividad" count={leadHistory.length} open={open.historial} onToggle={() => toggle('historial')} data-id="element-140" data-mp="2d9fos0rgwc18">
              <ol className="space-y-3" data-id="element-141" data-mp="2h9zl5247vbmb">
                {leadHistory.map(event => <li key={event.id} className="flex gap-3" data-id="element-142" data-mp="23jw72yfhqo7y">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-primary" data-id="element-143" data-mp="23hjgjwvo35ja" />
                    <div data-id="element-144" data-mp="28bnoojgo5jly">
                      <p className="text-sm text-foreground" data-id="element-145" data-mp="28c5qrwq0is1s">{event.description}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-146" data-mp="1tjdgzlxc2bhl">
                        {event.authorName} · {formatDateTime(event.createdAt)}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </Section>}

          {!essentials && <Section title="Archivos" count={leadFiles.length} open={open.archivos} onToggle={() => toggle('archivos')} data-id="element-147" data-mp="1pjbr0a0yn6i5">
              <Button variant="secondary" size="sm" className="mb-3 w-full" leftIcon={<PaperclipIcon className="h-4 w-4" data-id="element-149" data-mp="110jeg7abjaa1" />} onClick={() => fileRef.current?.click()} data-id="element-148" data-mp="1605u0ndmd2m0">
                Adjuntar archivo
              </Button>
              <input ref={fileRef} type="file" className="sr-only" onChange={e => {
            const file = e.target.files?.[0];
            if (file && lead) addFile(lead.organizationId, lead.id, file, authorName);
          }} data-id="element-150" data-mp="1axebsmuuv4mq" />
              {leadFiles.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-151" data-mp="1jxklryrchmh5">
                  No hay archivos adjuntos.
                </p> : <ul className="space-y-2" data-id="element-152" data-mp="1yotg2l66zw0m">
                  {leadFiles.map(file => <li key={file.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-id="element-153" data-mp="1toyrio9w3iiq">
                      <FileIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-154" data-mp="27nop050mfk7m" />
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground" data-id="element-155" data-mp="22q580xcjgtbz">
                        {file.name}
                      </span>
                      <button type="button" aria-label="Eliminar archivo" onClick={() => deleteFile(file.id)} className="rounded-md p-1.5 text-muted-foreground hover:text-red-600" data-id="element-156" data-mp="3ez318b2097bx">
                        <Trash2Icon className="h-4 w-4" data-id="element-157" data-mp="3o0akpmzv43ly" />
                      </button>
                    </li>)}
                </ul>}
            </Section>}
        </div>
      </aside>
    </>;
}